import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { T, Th, Tr, ActionBtn, Modal, TableCard, TableBar, SearchInput, Toast, PrimaryBtn, statusBadge, visibilityBadge } from './theme';
import { useRefresh } from './RefreshContext';
import API_BASE_URL from '../config/api';

const MyVideosSection = () => {
  const { role } = useAuth();
  const { refresh, triggerRefresh } = useRefresh();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState('');
  const [viewReview, setViewReview] = useState(null);
  const [editVideo, setEditVideo] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', youtube_url: '', author: '', category: '' });

  const userEmail = localStorage.getItem('vrvEmail') || '';
  const roles = (role || '').split(',').map(s => s.trim().toUpperCase());
  const isAdmin = roles.includes('ADMIN');
  const canDirectPublish = isAdmin || (roles.includes('CONTENT-CREATOR') && roles.includes('CONTENT-APPROVER'));

  const fetchMyVideos = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/vrv-youtubecontent-getAll`);
      const data = await res.json();
      const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
      setVideos((parsed.data || []).filter(v => (v.createdBy || '').toLowerCase() === userEmail.toLowerCase()).sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)));
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchMyVideos(); }, [userEmail, refresh]); // eslint-disable-line

  const showMsg = (message, type) => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3500);
  };

  const getYtId = (url) => { try { const u = new URL(url); return u.searchParams.get('v') || u.pathname.split('/').pop(); } catch { return null; } };

  const filtered = videos.filter(v => !search || Object.values(v).some(val => String(val).toLowerCase().includes(search.toLowerCase())));
  const toggleSelect = id => setSelectedIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleAll = () => setSelectedIds(selectedIds.length === filtered.length ? [] : filtered.map(v => v.content_id));

  const handleBulkAction = async (action) => {
    if (!selectedIds.length) { showMsg('Select at least one item', 'error'); return; }
    if ((action === 'PUBLIC' || action === 'PRIVATE') && filtered.some(v => selectedIds.includes(v.content_id) && v.status !== 'APPROVED')) {
      showMsg('Cannot change visibility for non-approved content', 'error'); return;
    }
    try {
      const token = localStorage.getItem('vrvToken');
      let ok = true;
      for (const content_id of selectedIds) {
        const res = await fetch(`${API_BASE_URL}/vrv-youtubecontent-action`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ content_id, action }) });
        if (!res.ok) ok = false;
      }
      showMsg(ok ? `${action} applied to ${selectedIds.length} item(s)` : 'Some failed', ok ? 'success' : 'error');
      setSelectedIds([]); fetchMyVideos(); triggerRefresh();
    } catch { showMsg('Error connecting', 'error'); }
  };

  const openEdit = (v) => { setEditVideo(v); setEditForm({ title: v.title || '', youtube_url: v.youtube_url || '', author: v.author || '', category: v.category || '' }); };
  const hasChanges = () => editVideo && (editForm.title !== (editVideo.title || '') || editForm.youtube_url !== (editVideo.youtube_url || '') || editForm.author !== (editVideo.author || '') || editForm.category !== (editVideo.category || ''));

  const handleEditSave = async () => {
    if (!editForm.title || !editForm.youtube_url || !editForm.author) { showMsg('Required fields missing', 'error'); return; }
    if (!hasChanges()) { showMsg('No changes to save', 'error'); return; }
    try {
      const token = localStorage.getItem('vrvToken');
      const res = await fetch(`${API_BASE_URL}/vrv-youtubecontent-action`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ content_id: editVideo.content_id, action: 'UPDATE', ...editForm, status: canDirectPublish ? 'APPROVED' : 'PENDING', visibility: canDirectPublish ? 'PUBLIC' : 'PRIVATE' }) });
      if (res.ok) { showMsg(canDirectPublish ? 'Video updated!' : 'Sent for re-approval!', 'success'); setEditVideo(null); fetchMyVideos(); triggerRefresh(); }
      else showMsg('Update failed', 'error');
    } catch { showMsg('Error connecting', 'error'); }
  };

  if (loading) return <div className="text-center py-16"><p className="text-sm" style={{ color: T.muted }}>Loading…</p></div>;
  if (!videos.length) return (
    <div className="vrv-card text-center py-16">
      <div className="text-4xl mb-3">▶️</div>
      <p className="font-semibold" style={{ color: T.muted }}>No videos uploaded by you yet.</p>
    </div>
  );

  return (
    <div>
      <div className="mb-4"><SearchInput value={search} onChange={e => setSearch(e.target.value)} /></div>

        {/* Desktop Table */}
        <div className="desktop-table">
          <TableCard>
            <TableBar className="table-bar-wrap">
              <p className="text-xs font-bold" style={{ color: T.muted }}>{selectedIds.length > 0 ? `${selectedIds.length} selected` : `${filtered.length} videos`}</p>
              <div className="flex flex-wrap gap-2">
                {[['Public', 'gold'], ['Private', 'muted'], ['Delete', 'red']].map(([label, color]) => (
                  <ActionBtn key={label} onClick={() => handleBulkAction(label.toUpperCase())} disabled={!selectedIds.length} color={color}>{label}</ActionBtn>
                ))}
              </div>
            </TableBar>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr>
                  <th className="px-3 py-3 w-8" style={{ background: T.stripe, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                    <input type="checkbox" checked={selectedIds.length === filtered.length && filtered.length > 0} onChange={toggleAll} />
                  </th>
                  <Th>Title</Th><Th>Category</Th><Th>URL</Th><Th>Date</Th><Th>Status</Th><Th>Visibility</Th><Th center>Edit</Th><Th center>Review</Th>
                </tr></thead>
                <tbody>
                  {filtered.map((v, i) => (
                    <Tr key={v.content_id || i} selected={selectedIds.includes(v.content_id)}>
                      <td className="px-3 py-2.5"><input type="checkbox" checked={selectedIds.includes(v.content_id)} onChange={() => toggleSelect(v.content_id)} /></td>
                      <td className="px-3 py-2.5 font-medium" style={{ color: T.ink }}>{v.title || '—'}</td>
                      <td className="px-3 py-2.5 text-xs" style={{ color: T.muted }}>{v.category}</td>
                      <td className="px-3 py-2.5"><a href={v.youtube_url} target="_blank" rel="noopener noreferrer" className="hover:underline truncate block max-w-[140px]" style={{ color: T.navy }}>{v.youtube_url}</a></td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-xs" style={{ color: T.muted }}>{v.createdAt ? new Date(v.createdAt).toLocaleDateString() : '—'}</td>
                      <td className="px-3 py-2.5"><span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest" style={statusBadge(v.status)}>{v.status || '—'}</span></td>
                      <td className="px-3 py-2.5"><span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest" style={visibilityBadge(v.visibility)}>{v.visibility}</span></td>
                      <td className="px-3 py-2.5 text-center"><ActionBtn onClick={() => openEdit(v)} color="gold" small>Edit</ActionBtn></td>
                      <td className="px-3 py-2.5 text-center">{v.reviewedBy ? <ActionBtn onClick={() => setViewReview(v)} color="muted" small>Review</ActionBtn> : <span style={{ color: T.muted }}>—</span>}</td>
                    </Tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TableCard>
        </div>

        {/* Mobile Cards */}
        <div className="mobile-cards">
          <div className="flex flex-wrap gap-2 mb-3">
            {[['Public', 'gold'], ['Private', 'muted'], ['Delete', 'red']].map(([label, color]) => (
              <ActionBtn key={label} onClick={() => handleBulkAction(label.toUpperCase())} disabled={!selectedIds.length} color={color}>{label}</ActionBtn>
            ))}
          </div>
          {filtered.map((v, i) => (
            <div key={v.content_id || i} className="vrv-mobile-card" style={{ border: selectedIds.includes(v.content_id) ? `1.5px solid ${T.gold}` : undefined }}>
              <div className="flex items-start gap-3 mb-2">
                <input type="checkbox" checked={selectedIds.includes(v.content_id)} onChange={() => toggleSelect(v.content_id)} className="mt-1" />
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm truncate" style={{ color: T.navy }}>{v.title || '—'}</p>
                  <p className="text-xs" style={{ color: T.muted }}>{v.category} · {v.createdAt ? new Date(v.createdAt).toLocaleDateString() : '—'}</p>
                </div>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest flex-shrink-0" style={statusBadge(v.status)}>{v.status || '—'}</span>
                <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase flex-shrink-0" style={visibilityBadge(v.visibility)}>{v.visibility}</span>
              </div>
              <div className="mb-3">
                {(() => { const ytId = getYtId(v.youtube_url); return ytId ? (
                  <a href={v.youtube_url} target="_blank" rel="noopener noreferrer" className="block relative rounded-xl overflow-hidden" style={{ aspectRatio: '16/9' }}>
                    <img src={`https://img.youtube.com/vi/${ytId}/mqdefault.jpg`} alt={v.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.3)' }}>
                      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.9)' }}>
                        <svg viewBox="0 0 24 24" className="w-5 h-5" style={{ fill: T.navy }}><path d="M8 5v14l11-7z"/></svg>
                      </div>
                    </div>
                  </a>
                ) : (
                  <a href={v.youtube_url} target="_blank" rel="noopener noreferrer" className="text-xs truncate block" style={{ color: T.navy }}>{v.youtube_url}</a>
                ); })()} 
              </div>
              <div className="flex gap-2 pt-2" style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                <ActionBtn onClick={() => openEdit(v)} color="gold" small>Edit</ActionBtn>
                {v.reviewedBy && <ActionBtn onClick={() => setViewReview(v)} color="muted" small>Review</ActionBtn>}
              </div>
            </div>
          ))}
        </div>

      {editVideo && (
        <Modal onClose={() => setEditVideo(null)} title="Edit Video" subtitle="My Content" wide>
          <div className="space-y-4">
            {[['Title *', 'title', 'text'], ['YouTube URL *', 'youtube_url', 'url'], ['Author *', 'author', 'text'], ['Category', 'category', 'text']].map(([label, name, type]) => (
              <div key={name}>
                <label className="vrv-label">{label}</label>
                <input type={type} value={editForm[name]} onChange={e => setEditForm({ ...editForm, [name]: e.target.value })} className="vrv-input" />
              </div>
            ))}
          </div>
          <div className="flex gap-3 mt-6">
            <ActionBtn onClick={() => setEditVideo(null)} color="muted">Cancel</ActionBtn>
            <button onClick={handleEditSave} disabled={!hasChanges()} className="btn-navy" style={{ opacity: hasChanges() ? 1 : 0.45 }}>
              {canDirectPublish ? 'Update & Publish →' : 'Update & Send for Approval →'}
            </button>
          </div>
        </Modal>
      )}

      {viewReview && (
        <Modal onClose={() => setViewReview(null)} title="Review Details" subtitle="Review Info">
          {[['Reviewed By', viewReview.reviewedBy], ['Reviewed At', viewReview.reviewedAt ? new Date(viewReview.reviewedAt).toLocaleString() : '—'], ['Comments', viewReview.reviewComments || 'No comments']].map(([label, val]) => (
            <div key={label} className="mb-4">
              <label className="vrv-label">{label}</label>
              <p className="text-sm px-3 py-2.5 rounded-xl whitespace-pre-wrap" style={{ background: '#F8F5EE', color: T.ink, border: '1px solid rgba(0,0,0,0.07)' }}>{val}</p>
            </div>
          ))}
          <div className="mt-4"><PrimaryBtn onClick={() => setViewReview(null)}>Close</PrimaryBtn></div>
        </Modal>
      )}

      <Toast toast={toast} onClose={() => setToast({ show: false, message: '', type: '' })} />
    </div>
  );
};

export default MyVideosSection;
